/**
 * App shell primitives — side-nav + top-bar layout (M6-01, §14.4).
 *
 * Composition, not a framework: the app owns routing and i18n; these
 * primitives own layout, mirroring and a11y. Grid areas use logical
 * placement, so the nav sits at the inline START — right side in AR,
 * left side in EN — with zero app code (§14.2).
 *
 *   <AppShell nav={<SideNav …/>} topBar={<TopBar …/>}>{routes}</AppShell>
 */

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { cx } from "../utils/cx";

/* ------------------------------------------------------------------ */
/* AppShell                                                            */
/* ------------------------------------------------------------------ */

export interface AppShellProps {
  readonly nav: ReactNode;
  readonly topBar: ReactNode;
  readonly children: ReactNode;
  /** Collapsed side-nav (icon rail). Controlled by the app. */
  readonly navCollapsed?: boolean;
  /** Skip-link text (i18n slot) — first tabbable element (§14.3). */
  readonly skipToContentLabel: string;
  readonly className?: string;
}

export function AppShell({ nav, topBar, children, navCollapsed = false, skipToContentLabel, className }: AppShellProps) {
  return (
    <div className={cx("ui-app", "ui-shell", navCollapsed && "ui-shell--nav-collapsed", className)}>
      <a className="ui-shell-skip ui-visually-hidden ui-focusable" href="#ui-shell-main">
        {skipToContentLabel}
      </a>
      {nav}
      <div className="ui-shell-column">
        {topBar}
        <main className="ui-shell-main" id="ui-shell-main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SideNav                                                             */
/* ------------------------------------------------------------------ */

export interface SideNavProps {
  /** Accessible name for the nav landmark (i18n slot). */
  readonly label: string;
  /** Brand slot — server-driven logo + app name (§14.1.1). */
  readonly brand?: ReactNode;
  readonly children: ReactNode;
  /** Pinned to the block end (user card, logout). */
  readonly footer?: ReactNode;
  readonly className?: string;
}

export function SideNav({ label, brand, children, footer, className }: SideNavProps) {
  return (
    <nav className={cx("ui-sidenav", className)} aria-label={label}>
      {brand ? <div className="ui-sidenav-brand">{brand}</div> : null}
      <div className="ui-sidenav-items">{children}</div>
      {footer ? <div className="ui-sidenav-footer">{footer}</div> : null}
    </nav>
  );
}

export interface SideNavSectionProps {
  /** Group heading (i18n slot); visually hidden when the nav is collapsed. */
  readonly title: ReactNode;
  readonly children: ReactNode;
}

export function SideNavSection({ title, children }: SideNavSectionProps) {
  return (
    <div className="ui-sidenav-section">
      <div className="ui-sidenav-section-title">{title}</div>
      {children}
    </div>
  );
}

interface SideNavItemBaseProps {
  readonly icon: ReactNode;
  /** Item text (i18n slot). Doubles as the accessible name when collapsed. */
  readonly label: string;
  readonly active?: boolean;
  /** Count/status adornment (e.g. pending orders). */
  readonly badge?: ReactNode;
}

export type SideNavItemProps = SideNavItemBaseProps &
  (
    | ({ readonly as?: "a" } & AnchorHTMLAttributes<HTMLAnchorElement>)
    | ({ readonly as: "button" } & ButtonHTMLAttributes<HTMLButtonElement>)
  );

/** Renders an <a> by default (router apps intercept onClick), or a <button>. */
export function SideNavItem(props: SideNavItemProps) {
  const { icon, label, active = false, badge } = props;
  const inner = (
    <>
      <span className="ui-sidenav-item-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="ui-sidenav-item-label">{label}</span>
      {badge != null ? <span className="ui-sidenav-item-badge">{badge}</span> : null}
    </>
  );
  const classes = cx("ui-sidenav-item", "ui-focusable", active && "ui-sidenav-item--active");
  // Strip component-only props before spreading onto the DOM element.
  const OWN_PROPS = new Set(["as", "icon", "label", "active", "badge", "className", "type"]);
  const domProps = (p: SideNavItemProps) =>
    Object.fromEntries(Object.entries(p).filter(([key]) => !OWN_PROPS.has(key)));
  if (props.as === "button") {
    const { className, type } = props;
    return (
      <button
        type={type ?? "button"}
        className={cx(classes, className)}
        aria-current={active ? "page" : undefined}
        title={label}
        {...domProps(props)}
      >
        {inner}
      </button>
    );
  }
  const { className } = props;
  return (
    <a className={cx(classes, className)} aria-current={active ? "page" : undefined} title={label} {...domProps(props)}>
      {inner}
    </a>
  );
}

/* ------------------------------------------------------------------ */
/* TopBar                                                              */
/* ------------------------------------------------------------------ */

export interface TopBarProps extends HTMLAttributes<HTMLElement> {
  /** Start slot — page title, breadcrumbs, nav-collapse toggle. */
  readonly start?: ReactNode;
  /** End slot — search, locale/mode switches, notifications, user menu. */
  readonly end?: ReactNode;
}

export function TopBar({ start, end, className, ...rest }: TopBarProps) {
  return (
    <header className={cx("ui-topbar", className)} {...rest}>
      <div className="ui-topbar-start">{start}</div>
      <div className="ui-topbar-end">{end}</div>
    </header>
  );
}
